import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, Box, ModalPortal, Stack } from '@/components';
import { modalBackdrop, modalController, modalWrapper } from '@/animations';
import classNames from 'classnames';

interface IModal {
  open: boolean;
  hideBackdrop?: boolean;
  onClose: () => void;
  contanerClassName?: string;
  children?: React.ReactNode;
  disableCloseOnBackdropClick?: boolean;
  // Overrides the default z-50 stacking — needed when the modal must render
  // above a host surface that already sets its own inline z-index (e.g. the
  // Nova panel at 999999).
  zIndex?: number;
}

const Modal = ({
  open: _open,
  hideBackdrop,
  onClose,
  contanerClassName,
  children,
  disableCloseOnBackdropClick,
  zIndex,
}: IModal) => {
  const [open, setOpen] = useState(false);
  const [animateDelay, setAnimateDelay] = useState(true);
  const isFirstRender = useRef(true);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!_open) {
        return;
      }
    }
    if (_open) {
      setOpen(true);
      setAnimateDelay(true);
    } else {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_open]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        const openedModalsElements = wrapperRef.current?.parentElement?.getElementsByClassName('modal');
        if (
          openedModalsElements &&
          openedModalsElements.length > 0 &&
          wrapperRef.current === openedModalsElements[openedModalsElements.length - 1]
        ) {
          close();
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscapeKey);
    } else {
      document.removeEventListener('keydown', handleEscapeKey);
    }
    return () => document.removeEventListener('keydown', handleEscapeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setAnimateDelay(false);
    if (onClose) onClose();
    setTimeout(() => {
      setOpen(false);
    }, 350);
  };

  return open ? (
    <ModalPortal>
      <Stack
        animation={modalWrapper}
        className="modal disable-scroll fixed top-0 z-50 flex h-screen w-screen flex-col items-center justify-center"
        style={zIndex ? { zIndex } : undefined}
        ref={wrapperRef}
      >
        <AnimatePresence>
          {animateDelay && (
            <>
              {!hideBackdrop && (
                <Box
                  animation={modalBackdrop}
                  className="absolute h-screen w-screen bg-black/50"
                  onClick={disableCloseOnBackdropClick ? undefined : close}
                />
              )}
              <Box animation={modalController} className={classNames('relative rounded-md', contanerClassName)}>
                {children}
              </Box>
            </>
          )}
        </AnimatePresence>
      </Stack>
    </ModalPortal>
  ) : null;
};

export default Modal;
